// Package Imports
import { useEffect } from 'react';
import { Col, Row } from 'antd';
import { useTranslate } from '@refinedev/core';
// Project Imports
import { useWatch } from 'antd/es/form/Form';
import DropdownInput from 'components/dropdown/dropDownInput';
import { initialValuesQueryBuilder } from 'pages/rules/RulesDummyData';
import RadioInput from 'components/radio/RadioInput';
import QueryBuilder from 'components/queryBuilder/query-builder';
import SimplePayoutBuilder from 'pages/rules/SimplePayoutBuilder';
import usePermission from 'core/utils/usePermission';
import PrivateRouteConst from 'core/common/privateRouteConstant';
import ConfigStyle from 'core/constants/ConfigStyle';
import TextInput from 'components/text/textInput';
import { useRulesCreateState } from 'pages/rules/context/RulesCreateStateContext';
// eslint-disable-next-line import/extensions
import useRulesDropdown from './RulesDropdown';

// Helper function to DRY up output_formula selection logic
function getOutputFormulaValue(form) {
  if (
    form.getFieldValue('output_formula_mode_type') === 'single' &&
    form.getFieldValue('output_formula')?.query
  ) {
    return form.getFieldValue('output_formula');
  }
  if (
    form.getFieldValue('output_formula_mode_type') === 'sql' &&
    form.getFieldValue('output_formula')?.caseList?.length > 0
  ) {
    return form.getFieldValue('output_formula');
  }
  return initialValuesQueryBuilder;
}

// Options for output formula type
const outputFormulaTypeOptions = [
  { label: 'Simple', value: 'simple' },
  { label: 'Advanced', value: 'advanced' },
];

const FeelExpressionForm = ({
  type,
  form,
  disabled,
  configFields,
  forceShow = false,
}) => {
  const t = useTranslate();
  const { modeOptions, dataType, editorOptions } = useRulesDropdown();
  const checkPermission = usePermission();
  const state = useRulesCreateState();

  const hasFormulaView = !!checkPermission(
    PrivateRouteConst.OUTPUT_FORMULA_VIEW
  );
  const hasFormulaEdit = !!checkPermission(
    PrivateRouteConst.OUTPUT_FORMULA_EDIT
  );
  const outputFormulaType = useWatch('output_formula_type', {
    form,
    preserve: true,
  });

  const outputFormulaModeType = useWatch('output_formula_mode_type', {
    form,
    preserve: true,
  });

  const outputFormulaEditorType = useWatch('output_formula_editor_type', {
    form,
    preserve: true,
  });

  console.log(outputFormulaType, outputFormulaEditorType, 'outputFormulaType');

  const handleOnQueryChange = (value) => {
    form.setFieldsValue({ output_formula: value });
  };

  const handleSimplePayoutChange = (value) => {
    const obj = {
      ...value,
      jsonLogic: {
        logic: value.jsonLogic,
      },
    };
    console.log(value);
    // Store the simple payout configuration
    form.setFieldsValue({
      output_formula: obj,
    });
  };

  const handleOutputFormulaType = (val) => {
    let obj;
    if (val?.target?.value === 'simple') {
      obj = {
        type: 'percentage',
        fields: {},
        jsonLogic: {},
        query: {},
        value: '',
      };
    } else {
      obj = {
        type: 'Query',
        fields: {},
        jsonLogic: {},
        query: {},
        value: '',
      };
    }
    form.setFieldsValue({
      output_formula_type: val?.target?.value,
      output_formula: obj,
      output_formula_editor_type: 'wizard',
    });
  };

  const newruleVersion = useWatch('rule_version', {
    form,
    preserve: true,
  });

  useEffect(() => {
    if (outputFormulaType && type === 'Create') {
      const obj = {
        target: {
          value: outputFormulaType,
        },
      };
      setTimeout(() => {
        handleOutputFormulaType(obj);
      }, 1000);
    }
  }, [outputFormulaType]);

  console.log(outputFormulaType, 'outputFormulaType');
  console.log(state.outputFormulaError, 'outputFormulaError');
  const effectiveType = forceShow ? 'Edit' : type;

  // NEW: hide entire formula section if user has no view permission
  if (!hasFormulaView) return null;

  return (
    <>
      <div className={effectiveType !== 'Show' ? 'block' : 'hidden'}>
        <Row gutter={18}>
          <Col sm={24} className="mb-sm">
            <span
              style={{
                fontWeight: ConfigStyle.labelfontWeight,
                color: ConfigStyle.labelColor,
              }}
              className="px-xs"
            >
              {t('rules.qbPayoutLabel')}
              <span className="a8-error"> *</span>
            </span>
          </Col>

          <Col xs={24} sm={24} lg={10} className="hidden">
            <TextInput
              label={t('rules.isReportingPayoutVariable')}
              name="reporting_payout_variable_name"
              required={false}
              message={t('rules.isReportingPayoutVariable')}
              placeholder={t('rules.isReportingPayoutVariable')}
              disabled={disabled}
            />
          </Col>

          <div className="hidden">
            <Col xs={24} sm={24} lg={10}>
              <DropdownInput
                label="Type"
                name="reporting_payout_variable_type"
                required={true}
                optionList={dataType}
                message="Select Type"
                placeholder="Select Type"
                disabled={disabled}
              />
            </Col>
          </div>

          {/* New: Output Formula Type Selection */}
          <Col xs={24} sm={24} lg={24} className="radio-label">
            <RadioInput
              label={t('rules.selectFormulaType') || 'Select Formula Type'}
              name="output_formula_type"
              defaultValue="simple"
              options={outputFormulaTypeOptions}
              disabled={disabled || !hasFormulaEdit}
              onChange={handleOutputFormulaType}
            />
          </Col>

          {/* Show mode type only for advanced formula type */}
          {outputFormulaType === 'advanced' ? (
            <div className={type !== 'Show' ? 'block w-full' : 'hidden'}>
              {outputFormulaEditorType === 'wizard' &&
              Object.keys(configFields)?.length > 0 ? (
                <Col xs={24} sm={24} lg={24} className="radio-label">
                  <RadioInput
                    label={t('rules.selectModeType')}
                    name="output_formula_mode_type"
                    defaultValue="single"
                    options={modeOptions}
                    disabled={disabled || !hasFormulaEdit}
                  />
                </Col>
              ) : null}
            </div>
          ) : null}
        </Row>
      </div>

      {/* Show editor type only for advanced formula type */}
      {outputFormulaType === 'advanced' ? (
        <div className={effectiveType !== 'Show' ? 'block' : 'hidden'}>
          <Col xs={24} sm={24} lg={18} className="radio-label">
            <RadioInput
              label={t('rules.selectEditorType')}
              name="output_formula_editor_type"
              defaultValue="wizard"
              options={editorOptions}
              disabled={disabled || !hasFormulaEdit}
              style={{ display: 'none' }}
            />
          </Col>
        </div>
      ) : null}

      {/* Conditional rendering based on formula type */}
      {outputFormulaType === 'simple' && configFields?.length > 0 ? (
        // Simple Payout Builder
        <div key={JSON.stringify(newruleVersion)}>
          <Row gutter={16} className="mb-0">
            <Col xs={24} sm={24} lg={24}>
              <SimplePayoutBuilder
                configFields={configFields}
                value={form.getFieldValue('output_formula')}
                onChange={handleSimplePayoutChange}
                disabled={
                  disabled || effectiveType === 'Show' || !hasFormulaEdit
                }
                showButtons={
                  checkPermission(PrivateRouteConst.SHOW_BUTTON) || false
                }
                form={form} // Pass the form instance
                name="output_formula" // The field name in the form
              />
            </Col>
          </Row>
        </div>
      ) : (
        // Advanced Query Builder (existing functionality)
        <div key={JSON.stringify(newruleVersion)}>
          <Row gutter={16} className="mb-0">
            <Col xs={24} sm={24} lg={24}>
              <QueryBuilder
                label="Payout Formula"
                configFields={{
                  mode: outputFormulaModeType,
                  columns: configFields,
                }}
                queryValue=""
                handleOnQueryChange={handleOnQueryChange}
                setExpression={handleOnQueryChange}
                expressionValue={getOutputFormulaValue(form)}
                showButtons={
                  checkPermission(PrivateRouteConst.SHOW_BUTTON) || false
                }
                showEditor={type !== 'Show' ? hasFormulaEdit : false}
                key={JSON.stringify(configFields)}
              />
            </Col>
          </Row>
        </div>
      )}

      {state.isSavedClick && state.outputFormulaError ? (
        <p className="a8-error">{t('rules.messages.payoutFormulaQbError')}</p>
      ) : null}
    </>
  );
};

export default FeelExpressionForm;
